import React, { useState } from 'react';
import { XIcon, AlertCircleIcon, CheckCircleIcon, FileTextIcon, AlertTriangleIcon } from 'lucide-react';
type DBRSExportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  templateType: string;
  reportingPeriod: string;
};
export function DBRSExportModal({
  isOpen,
  onClose,
  templateType,
  reportingPeriod
}: DBRSExportModalProps) {
  const [selectedFormat, setSelectedFormat] = useState('xlsx');
  const [includeFieldComments, setIncludeFieldComments] = useState(true);
  const [includeSourceInfo, setIncludeSourceInfo] = useState(true);
  if (!isOpen) return null;
  const handleExport = () => {
    // In a real app, this would generate and download the export
    alert('Exporting template... This would download the file in a real application.');
    onClose();
  };
  const handleResolveIssues = () => {
    // In a real app, this would navigate to the exceptions page
    alert('Navigating to resolve issues... This would take you to the exceptions page in a real application.');
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto" data-id="element-291">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-xl mx-auto my-4" data-id="element-292">
        <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-293">
          <h2 className="text-lg font-semibold text-gray-800" data-id="element-294">
            Export DBRS Surveillance Template
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-295">
            <XIcon className="w-5 h-5" data-id="element-296" />
          </button>
        </div>
        <div className="p-4" data-id="element-297">
          <p className="text-gray-600 mb-4 text-sm" data-id="element-298">
            Configure your export settings and review validation warnings before
            proceeding.
          </p>
          <div className="space-y-4" data-id="element-299">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-300">
              <div data-id="element-301">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-302">
                  Template Type
                </label>
                <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-1.5 bg-gray-50 text-sm" value={templateType} disabled data-id="element-303" />
              </div>
              <div data-id="element-304">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-305">
                  Reporting Period
                </label>
                <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-1.5 bg-gray-50 text-sm" value={reportingPeriod} disabled data-id="element-306" />
              </div>
            </div>
            <div data-id="element-307">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-308">
                Format Options
              </label>
              <div className="flex flex-wrap gap-4" data-id="element-309">
                {['xlsx', 'csv', 'json'].map(format => <label key={format} className="flex items-center" data-id="element-310">
                    <input type="radio" name="format" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={selectedFormat === format} onChange={() => setSelectedFormat(format)} data-id="element-311" />
                    <span className="ml-2 text-sm text-gray-700 uppercase" data-id="element-312">
                      {format}
                    </span>
                  </label>)}
              </div>
            </div>
            <div data-id="element-313">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-314">
                Include Sections
              </label>
              <div className="space-y-2" data-id="element-315">
                <label className="flex items-center" data-id="element-316">
                  <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded border-gray-300" checked={includeFieldComments} onChange={() => setIncludeFieldComments(!includeFieldComments)} data-id="element-317" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-318">
                    Field Comments
                  </span>
                </label>
                <label className="flex items-center" data-id="element-319">
                  <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded border-gray-300" checked={includeSourceInfo} onChange={() => setIncludeSourceInfo(!includeSourceInfo)} data-id="element-320" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-321">
                    Source Info
                  </span>
                </label>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-322">
              <h3 className="text-sm font-medium text-gray-800 mb-2" data-id="element-323">
                Validation Summary
              </h3>
              <div className="space-y-2" data-id="element-324">
                <div className="flex justify-between items-center" data-id="element-325">
                  <div className="flex items-center" data-id="element-326">
                    <FileTextIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-327" />
                    <span className="text-sm text-gray-700" data-id="element-328">
                      Fields Complete
                    </span>
                  </div>
                  <span className="text-sm font-medium" data-id="element-329">92 of 98</span>
                </div>
                <div className="flex justify-between items-center" data-id="element-330">
                  <div className="flex items-center" data-id="element-331">
                    <AlertCircleIcon className="w-4 h-4 text-red-500 mr-2" data-id="element-332" />
                    <span className="text-sm text-gray-700" data-id="element-333">
                      Critical Exceptions
                    </span>
                  </div>
                  <span className="text-sm font-medium text-red-600" data-id="element-334">
                    3 (blocking export)
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-335">
                  <div className="flex items-center" data-id="element-336">
                    <AlertTriangleIcon className="w-4 h-4 text-amber-500 mr-2" data-id="element-337" />
                    <span className="text-sm text-gray-700" data-id="element-338">Warnings</span>
                  </div>
                  <span className="text-sm font-medium" data-id="element-339">2</span>
                </div>
              </div>
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-id="element-340">
                <div className="flex items-start" data-id="element-341">
                  <AlertTriangleIcon className="w-4 h-4 text-amber-500 mr-1.5 mt-0.5 flex-shrink-0" data-id="element-342" />
                  <div data-id="element-343">
                    <p className="text-xs font-medium text-amber-800" data-id="element-344">
                      FICO Score is unresolved
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-345">
                      The FICO Score field is missing for 3 loans. This is
                      required for DBRS submission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="pt-3 border-t border-gray-200" data-id="element-346">
              <h3 className="text-sm font-medium text-gray-800 mb-2" data-id="element-347">
                Data Consistency
              </h3>
              <div className="overflow-x-auto max-h-32 sm:max-h-48" data-id="element-348">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-349">
                  <thead className="bg-gray-50" data-id="element-350">
                    <tr data-id="element-351">
                      <th scope="col" className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-352">
                        Field Name
                      </th>
                      <th scope="col" className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-353">
                        Example Value
                      </th>
                      <th scope="col" className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-354">
                        Source
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-355">
                    <tr data-id="element-356">
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-900" data-id="element-357">
                        FICO Score
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500" data-id="element-358">
                        720
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500" data-id="element-359">
                        Equifax API
                      </td>
                    </tr>
                    <tr data-id="element-360">
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-900" data-id="element-361">
                        Loan Amount
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500" data-id="element-362">
                        $435,000
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500" data-id="element-363">
                        Loan Agreement via OCR
                      </td>
                    </tr>
                    <tr data-id="element-364">
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-900" data-id="element-365">
                        Reporting Period
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500" data-id="element-366">
                        Q3 2025
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-500" data-id="element-367">
                        Fixed across all surfaces
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg" data-id="element-368">
          <button className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 mr-2" onClick={onClose} data-id="element-369">
            Cancel
          </button>
          <button className="px-3 py-1.5 bg-yellow-600 rounded-md text-sm text-white hover:bg-yellow-700 mr-2" onClick={handleResolveIssues} data-id="element-370">
            Resolve Issues
          </button>
          <button className="px-3 py-1.5 bg-blue-600 rounded-md text-sm text-white hover:bg-blue-700" onClick={handleExport} data-id="element-371">
            Export Template
          </button>
        </div>
      </div>
    </div>;
}